import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
// Gentle water ripple that expands slowly like a drop in still water
interface WaterRippleProps {
  size?: number;
  color?: string;
  style?: React.CSSProperties;
  delay?: number;
  onComplete?: () => void;
}
function WaterRipple({
  size = 60,
  color = 'rgba(126, 184, 218, 0.3)',
  style,
  delay = 0,
  onComplete
}: WaterRippleProps) {
  return <motion.div className="absolute" style={style} initial={{
    opacity: 1
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.5
  }} data-id="element-199">
      {/* Slow, meditative expanding rings */}
      {[0, 1, 2].map(ring => <motion.div key={ring} className="absolute rounded-full" style={{
      width: size,
      height: size,
      border: `1px solid ${color}`,
      left: -size / 2,
      top: -size / 2
    }} initial={{
      scale: 0,
      opacity: 0.6
    }} animate={{
      scale: [0, 4, 6],
      opacity: [0.6, 0.3, 0]
    }} transition={{
      duration: 12,
      delay: delay + ring * 2,
      ease: 'easeOut'
    }} onAnimationComplete={ring === 2 ? onComplete : undefined} data-id="element-200" />)}
    </motion.div>;
}
// Soft glowing orb that pulses gently like light through water
interface GlowOrbProps {
  size?: number;
  color?: string;
  style?: React.CSSProperties;
  delay?: number;
}
function GlowOrb({
  size = 8,
  color = '#7EB8DA',
  style,
  delay = 0
}: GlowOrbProps) {
  return <motion.div className="absolute rounded-full" style={{
    width: size,
    height: size,
    backgroundColor: color,
    boxShadow: `0 0 ${size * 3}px ${color}, 0 0 ${size * 6}px ${color}40`,
    ...style
  }} animate={{
    opacity: [0.3, 0.7, 0.3],
    scale: [1, 1.2, 1]
  }} transition={{
    duration: 6,
    repeat: Infinity,
    delay: delay,
    ease: 'easeInOut'
  }} data-id="element-201" />;
}
// Calm neon pond colors - brighter
const calmNeonColors = ['rgba(140, 200, 235, 0.7)', 'rgba(180, 160, 255, 0.65)', 'rgba(130, 240, 200, 0.6)', 'rgba(165, 210, 255, 0.7)', 'rgba(210, 195, 255, 0.65)'];
const glowColors = ['#7EB8DA', '#A78BFA', '#6EE7B7', '#93C5FD', '#C4B5FD' // Violet
];
interface RippleData {
  id: number;
  size: number;
  color: string;
  style: {
    top: string;
    left: string;
  };
  delay: number;
}
function generateRipple(id: number, colors: string[]): RippleData {
  return {
    id,
    size: Math.random() * 40 + 40,
    color: colors[Math.floor(Math.random() * colors.length)],
    style: {
      top: `${Math.random() * 100}%`,
      left: `${Math.random() * 100}%`
    },
    delay: 0
  };
}
interface SparklesProps {
  count?: number;
  className?: string;
  colors?: string[];
}
export function Sparkles({
  count = 12,
  className = '',
  colors = calmNeonColors
}: SparklesProps) {
  const [ripples, setRipples] = useState<RippleData[]>([]);
  const [idCounter, setIdCounter] = useState(0);
  // Stagger the initial appearance of ripples dramatically
  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    for (let i = 0; i < count; i++) {
      const timer = setTimeout(() => {
        setRipples(prev => [...prev, {
          ...generateRipple(i, colors),
          delay: 0
        }]);
        setIdCounter(i + 1);
      }, i * 2500 + Math.random() * 1500); // Stagger each ripple by 2.5-4 seconds
      timers.push(timer);
    }
    return () => timers.forEach(t => clearTimeout(t));
  }, [count, colors]);
  const handleRippleComplete = (completedId: number) => {
    // Add a longer random delay before spawning new ripple for continuous feel
    const randomDelay = Math.random() * 5000 + 3000; // 3-8 seconds delay
    setTimeout(() => {
      setRipples(prev => prev.map(ripple => ripple.id === completedId ? generateRipple(Date.now(), colors) : ripple));
      setIdCounter(prev => prev + 1);
    }, randomDelay);
  };
  const orbs = useMemo(() => {
    return Array.from({
      length: Math.floor(count / 2)
    }, (_, i) => ({
      id: i,
      size: Math.random() * 6 + 3,
      color: glowColors[Math.floor(Math.random() * glowColors.length)],
      style: {
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`
      },
      delay: Math.random() * 6
    }));
  }, [count]);
  return <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} data-id="element-202">
      <AnimatePresence mode="popLayout" data-id="element-203">
        {ripples.map(ripple => <WaterRipple key={ripple.id} size={ripple.size} color={ripple.color} style={ripple.style} delay={ripple.delay} onComplete={() => handleRippleComplete(ripple.id)} data-id="element-204" />)}
      </AnimatePresence>
      {orbs.map(orb => <GlowOrb key={`orb-${orb.id}`} size={orb.size} color={orb.color} style={orb.style} delay={orb.delay} data-id="element-205" />)}
    </div>;
}
// Floating light motes that drift slowly upward like reflections
export function FloatingParticles({
  count = 10
}: {
  count?: number;
}) {
  const particles = useMemo(() => {
    return Array.from({
      length: count
    }, (_, i) => ({
      id: i,
      size: Math.random() * 4 + 2,
      x: Math.random() * 100,
      delay: Math.random() * 10,
      duration: Math.random() * 20 + 25,
      color: glowColors[Math.floor(Math.random() * glowColors.length)]
    }));
  }, [count]);
  return <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-206">
      {particles.map(particle => <motion.div key={particle.id} className="absolute rounded-full" style={{
      width: particle.size,
      height: particle.size,
      backgroundColor: particle.color,
      left: `${particle.x}%`,
      bottom: '-5%',
      boxShadow: `0 0 ${particle.size * 4}px ${particle.color}`,
      filter: 'blur(0.5px)'
    }} animate={{
      y: [0, -800],
      opacity: [0, 0.6, 0.6, 0],
      x: [0, Math.sin(particle.id) * 30] // Gentle sway
    }} transition={{
      duration: particle.duration,
      repeat: Infinity,
      delay: particle.delay,
      ease: 'linear'
    }} data-id="element-207" />)}
    </div>;
}
// Single accent ripple for decorative placement
export function AccentRipple({
  color = 'rgba(126, 184, 218, 0.4)',
  size = 50,
  className = ''
}: {
  color?: string;
  size?: number;
  className?: string;
}) {
  return <div className={`absolute ${className}`} data-id="element-208">
      {[0, 1, 2].map(ring => <motion.div key={ring} className="absolute rounded-full" style={{
      width: size,
      height: size,
      border: `1px solid ${color}`,
      left: -size / 2,
      top: -size / 2
    }} initial={{
      scale: 0,
      opacity: 0.5
    }} animate={{
      scale: [0, 2.5, 3],
      opacity: [0.4, 0.15, 0]
    }} transition={{
      duration: 6,
      repeat: Infinity,
      delay: ring * 1,
      ease: 'easeOut'
    }} data-id="element-209" />)}
      {/* Soft center glow */}
      <motion.div className="absolute rounded-full" style={{
      width: size / 4,
      height: size / 4,
      backgroundColor: color.replace(/[\d.]+\)$/, '0.6)'),
      left: -size / 8,
      top: -size / 8,
      boxShadow: `0 0 ${size / 2}px ${color}`
    }} animate={{
      opacity: [0.4, 0.7, 0.4]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-210" />
    </div>;
}
// Horizontal water surface reflection line
export function WaterReflection({
  className = ''
}: {
  className?: string;
}) {
  return <div className={`absolute w-full pointer-events-none ${className}`} data-id="element-211">
      <motion.div className="h-px w-full bg-gradient-to-r from-transparent via-pond-blue/30 to-transparent" animate={{
      opacity: [0.3, 0.6, 0.3],
      scaleX: [0.8, 1, 0.8]
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-212" />
    </div>;
}